import React from "react"
import { ActionDropdown } from "components/common"
import {
  FaFolder,
  FaFile,
  FaEllipsisV,
  FaCheck,
  FaDownload,
  FaEye,
  FaEdit,
  FaTrash,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaFileArchive,
  FaFileCode,
  FaUserPlus
} from "react-icons/fa";
import PropTypes from 'prop-types';
import { ONLYOFFICE_SUPPORTED_TYPES } from "components/app/drive/hooks/useFileIcon"

// Unified folder color across the whole Template module (app blue) — folders are
// NOT rainbow-colored per item. Files keep type-based icon colors + soft tints.
const FOLDER_ICON = "#2563eb"
const FOLDER_TINT = "#eff6ff"

// icon + color + pastel panel tint per file kind
const FILE_META = [
  { test: (ct) => ct.includes("pdf"), icon: FaFilePdf, color: "#dc3545", tint: "#fef2f2" },
  { test: (ct) => ct.includes("word") || ct.includes("document"), icon: FaFileWord, color: "#2563eb", tint: "#eff6ff" },
  { test: (ct) => ct.includes("excel") || ct.includes("spreadsheet"), icon: FaFileExcel, color: "#198754", tint: "#f0fdf4" },
  { test: (ct) => ct.includes("image"), icon: FaFileImage, color: "#0891b2", tint: "#f0f9ff" },
  { test: (ct) => ct.includes("zip") || ct.includes("compressed"), icon: FaFileArchive, color: "#d97706", tint: "#fff7ed" },
  { test: (ct) => ct.includes("javascript") || ct.includes("json"), icon: FaFileCode, color: "#7c3aed", tint: "#eef2ff" },
]

const TemplatePolicyCard = ({ item, onAction, canRename, canDelete, isSelected, onSelect }) => {
  const isFolder = item.asset_type === "folder"
  const { allow_edit_document_online, allow_assign_owner } = item?.permissions || {}
  const canOpenInOnlyOffice = !isFolder && ONLYOFFICE_SUPPORTED_TYPES.includes(item?.content_type)
  const canEditOnline = allow_edit_document_online && canOpenInOnlyOffice

  const meta = isFolder
    ? { icon: FaFolder, color: FOLDER_ICON, tint: FOLDER_TINT }
    : (FILE_META.find(({ test }) => test(item.content_type || "")) || { icon: FaFile, color: "#64748b", tint: "#f1f5f9" })
  const { icon: Icon, color, tint } = meta

  const showThumb = !isFolder && (item.content_type || "").includes("image") && item.preview_url

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B"
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB"
    return (bytes / 1048576).toFixed(1) + " MB"
  }

  const handleCardClick = () => {
    if (isFolder) onAction(item, "open-folder")
    else if (canOpenInOnlyOffice) onAction(item, "view-online")
    else onAction(item, "preview")
  }

  const dropdownItems = [
    ...(!isFolder ? [
      { label: 'View', icon: <FaEye />, onClick: () => onAction(item, "preview") },
      ...(canOpenInOnlyOffice ? [{
        label: allow_edit_document_online ? "Edit Online (OnlyOffice)" : "View in OnlyOffice",
        icon: <FaEdit className="tw-text-green-600" />,
        onClick: () => onAction(item, "edit-online")
      }] : []),
      { divider: true },
    ] : []),
    { label: 'Download', icon: <FaDownload />, onClick: () => onAction(item, "download") },
    { divider: true },
    ...(canRename ? [
      { label: 'Rename', icon: <FaEdit />, onClick: () => onAction(item, "rename") },
    ] : []),
    ...(allow_assign_owner && !isFolder ? [
      { label: 'Assign Owner', icon: <FaUserPlus className="tw-text-blue-600" />, onClick: () => onAction(item, "assign-owner") },
    ] : []),
    ...(canDelete ? [
      { divider: true },
      { label: 'Delete', icon: <FaTrash />, danger: true, onClick: () => onAction(item, "delete") },
    ] : []),
  ]

  return (
    <div
      onClick={handleCardClick}
      className="tw-group tw-relative tw-bg-white tw-rounded-lg tw-overflow-hidden tw-cursor-pointer tw-select-none tw-transition-all tw-duration-200 hover:tw-shadow-lg hover:-tw-translate-y-0.5"
      style={{
        border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
        boxShadow: isSelected ? '0 0 0 3px rgba(37,99,235,0.15)' : 'none',
      }}
    >
      {/* Thumbnail / icon panel */}
      <div
        className="tw-relative tw-w-full tw-flex tw-items-center tw-justify-center tw-overflow-hidden"
        style={{ aspectRatio: '16 / 10', backgroundColor: tint, borderBottom: '1px solid #f1f5f9' }}
      >
        {showThumb ? (
          <img src={item.preview_url} alt={item.name} className="tw-w-full tw-h-full tw-object-cover" loading="lazy" />
        ) : (
          <Icon size={48} style={{ color, opacity: 0.9 }} />
        )}

        {/* Checkbox — top left, reveals on hover */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSelect && onSelect(item.id) }}
          aria-label={isSelected ? 'Deselect' : 'Select'}
          className={`tw-absolute tw-top-2 tw-left-2 tw-z-[5] tw-w-[22px] tw-h-[22px] tw-flex tw-items-center tw-justify-center tw-rounded-md tw-cursor-pointer tw-shadow-sm tw-transition-opacity ${
            isSelected ? 'tw-opacity-100' : 'tw-opacity-0 group-hover:tw-opacity-100'
          }`}
          style={{
            backgroundColor: isSelected ? '#2563eb' : 'rgba(255,255,255,0.95)',
            color: isSelected ? '#ffffff' : 'transparent',
            border: `1px solid ${isSelected ? '#2563eb' : '#cbd5e1'}`,
          }}
        >
          {isSelected && <FaCheck size={10} />}
        </button>

        {/* Kebab — top right, white pill */}
        <div className="tw-absolute tw-top-1.5 tw-right-1.5 tw-z-[5]" onClick={(e) => e.stopPropagation()}>
          <ActionDropdown
            align="end"
            menuWidth={230}
            unstyledTrigger
            triggerClassName="tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-bg-white/95 tw-text-slate-600 tw-border tw-border-solid tw-border-slate-200 tw-rounded-md tw-cursor-pointer tw-shadow-sm hover:tw-bg-slate-900 hover:tw-text-white hover:tw-border-slate-900 tw-transition-colors"
            trigger={<FaEllipsisV size={11} />}
            items={dropdownItems}
          />
        </div>
      </div>

      {/* Meta */}
      <div className="tw-px-3.5 tw-pt-2.5 tw-pb-3">
        <p className="tw-flex tw-items-center tw-gap-1 tw-truncate tw-m-0 tw-text-[13px] tw-font-bold tw-text-slate-900 tw-leading-snug" title={item.name}>
          <span className="tw-truncate">{item.name}</span>
          {canEditOnline && (
            <span className="tw-shrink-0 tw-text-green-600" title="You can edit this document online">✎</span>
          )}
        </p>
        <p className="tw-truncate tw-m-0 tw-mt-0.5 tw-text-[11px] tw-font-medium tw-text-slate-400 tw-leading-snug">
          {isFolder ? 'Folder' : formatFileSize(item.file_size)}
          {item.created_date && <> · {item.created_date}</>}
        </p>

        {/* Created-by chip */}
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mt-2 tw-min-w-0">
          <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-[18px] tw-h-[18px] tw-rounded-xl tw-bg-slate-100 tw-text-[9px] tw-font-bold tw-text-slate-500 tw-shrink-0">
            {(item.created_by || "?").charAt(0).toUpperCase()}
          </span>
          <span className="tw-truncate tw-text-[11px] tw-text-slate-500">{item.created_by || "Unknown"}</span>
        </div>
      </div>
    </div>
  )
}

TemplatePolicyCard.propTypes = {
  item: PropTypes.object.isRequired,
  onAction: PropTypes.func.isRequired,
  canRename: PropTypes.bool,
  canDelete: PropTypes.bool,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
};

export default TemplatePolicyCard
